import {
    Component,
    Input,
    forwardRef,
    OnDestroy,
    Output,
    OnChanges,
    EventEmitter,
    AfterViewInit,
    ViewChild,
    ElementRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AbstractFormFieldComponent } from '@Common/PageBuilder/components/FormField/abstract-form-field.component';
import { AdvancedColorPickerFieldModel, AdvancedColorPickerConfig } from '@Common/PageBuilder/models/advanced-color-picker-field.model';
import { Subscription } from 'rxjs';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { ColorChangeModel } from '@Common/Forms/models/color-change.model';
import { ColorPickerSourceType } from '@Common/Forms/enums/color-picker-source-types.enum';

@Component({
    selector: 'agpr-color-picker-field',
    templateUrl: './color-picker-field.component.html',
    styleUrls: ['./color-picker-field.component.scss'],
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ColorPickerFieldComponent),
            multi: true
        }
    ],
})
export class ColorPickerFieldComponent extends AbstractFormFieldComponent implements ControlValueAccessor, OnDestroy, AfterViewInit, OnChanges {

    @ViewChild('colorInput') colorInputRef!: ElementRef<HTMLInputElement>;

    @Input() declare value: string; // color Value

    @Input() field: AdvancedColorPickerFieldModel = AdvancedColorPickerFieldModel.create();
    @Input()
    set config(config: AdvancedColorPickerConfig | undefined) {
        // Combine all separate inputs into a single config
        this.field = AdvancedColorPickerFieldModel.create(config);
    }

    @Input()
    set showPicker(show: boolean) {
        this._showPicker = show;
        if (!this._showPicker) {
            this.isPopoverOpen = false;
        }
    }
    @Input() brandId?: number;

    @Output()
    remove = new EventEmitter<string>();
    @Output()
    favoriteColorsChange = new EventEmitter<string[]>();

    readonly INPUT_MAX_SIZE = 6;
    readonly buttonTypes = ButtonTypesEnum;

    _showPicker: boolean = true;
    inputValue = '';
    subscriptions: Subscription[] = [];
    isPopoverOpen: boolean = false;
    recentColors: string[] = [];

    ngAfterViewInit() {
        if (this.field.initSelection && this.colorInputRef) {
            this.colorInputRef.nativeElement.focus();
        }
        if (!this.value) {
            const blockColorMap = this.block?.displayElement?.styles?.colorMap[this.field.key];
            if (blockColorMap) {
                this.setValue(blockColorMap, null);
            } else if (this.field.initialValue) {
                this.setValue(this.field.initialValue, null);
            }
        } else {
            this.setValue(this.value, null);
        }
    }

    ngOnChanges(): void {
        if (this.field && this.field.appearCondition) {
            this.showPicker = this.field.appearCondition(this.block.displayElement);
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.onModelTouched();
    }

    onColorSelectedWithType({value, source}: ColorChangeModel) {
        if (this.field?.applyHueChange === false && source === ColorPickerSourceType.HUE) {
            return;
        }
        this.setValue(value);
        this.onModelChange(this.value);
    }

    onHexInputValue(event: Event | ClipboardEvent) {
        let value = '';
        const inputElement = (event.target as HTMLInputElement);

        if (event instanceof ClipboardEvent) {
            value = event.clipboardData?.getData('text') || '';
        } else {
            value = (event.target as HTMLInputElement).value;
        }

        const formattedValue = (value || '')
            .trim()
            .replace(/[^0-9A-Fa-f]/g, '') // remove invalid chars
            .replace(/^#+/, '#')
            .slice(0, this.INPUT_MAX_SIZE);

        if (formattedValue.length >= this.INPUT_MAX_SIZE) {
            this.setValue(`#${formattedValue}`, inputElement);
        } else {
            // Wait until the value has enough characters
            // Invalid value will be handled on focus out
            this.setValueToInput(formattedValue, inputElement);
        }
        event.preventDefault();
    }

    onHexInputCopy(event: ClipboardEvent) {
        event.clipboardData?.setData('text/plain', this.value.toUpperCase());
        event.preventDefault();
    }

    onHexInputEnter(event: Event) {
        const taget = event.target as HTMLInputElement;
        if (taget) {
            this.onHexInputValue(event);
            taget.blur();
        }
    }

    onHexFocusOut(event: Event) {
        const inputElement = (event?.target as HTMLInputElement);
        if (!inputElement) {
            return;
        }
        const value = inputElement?.value;
        if (value.length < this.INPUT_MAX_SIZE) {
            // Reset to the valid value
            this.setValueToInput(`${this.value}`, inputElement);
        } else {
            this.setValue(`#${value.slice(0, this.INPUT_MAX_SIZE)}`, inputElement);
        }
        event.preventDefault();
    }

    onHexInputFocus($event: Event) {
        const copyText: HTMLInputElement = $event.target as HTMLInputElement;
        if (copyText) {
            copyText.select();
            copyText.setSelectionRange(0, 99999);
        }
        $event.preventDefault();
    }

    setValue(value: string, target?: HTMLInputElement | null): void {
        this.setValueToInput(value, target);

        const previousValue = this.value || '';
        const nenwValue = value || '';

        // Ignore case differences to avoid unnecessary emits
        if (previousValue.toUpperCase() !== nenwValue.toUpperCase()) {
            this.value = nenwValue;
            if (this.block?.displayElement?.styles?.colorMap && this.field?.key) {
                this.block.displayElement.styles.colorMap[this.field.key] = nenwValue;
            }
            this.changes.emit(nenwValue);
            this.onModelChange(nenwValue);
            this.onModelTouched();
        }
    }

    setValueToInput(value: string, target?: HTMLInputElement | null) {
        this.inputValue = (value || '').replace(/^#/, '').slice(0, this.INPUT_MAX_SIZE).toUpperCase();
        if (target) {
            // Sync inputValue with the transformed result
            target.value = this.inputValue;
        }
    }

    deleteFavoriteColor(value: string) {
        if (value) {
            this.remove.emit(value);
        }
    }

    onfavoriteColorsChange(colors: string[]) {
        this.favoriteColorsChange.emit(colors);
    }

    onPopoverOpenChange(open: boolean) {
        const wasOpen = this.isPopoverOpen;
        this.isPopoverOpen = open;
        // Only restore focus on a genuine open -> close transition. The overlay's
        // `(detach)` output can emit a close before the popover was ever opened
        // (e.g. right after the field is created), which would otherwise steal
        // focus from whatever the user just interacted with (like the CKEditor).
        if (wasOpen && !this.isPopoverOpen) {
            requestAnimationFrame(() => {
                this.colorInputRef?.nativeElement?.focus();
            });
        }
    }

    // ControlValueAccessor implementation
    onModelChange: Function = () => {};
    onModelTouched: Function = () => {};

    writeValue(value: string): void {
        this.value = value;
        this.setValueToInput(value, null);
    }

    registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

}
